import React, { useState } from 'react';
import { Save, AlertCircle, Check, Mail, Globe } from 'lucide-react';
import { User } from '../lib/types';
import { saveApiKey, testApiConnection } from '../lib/admin';
import SmtpSettingsPanel from './SmtpSettingsPanel';
import { Link } from 'react-router-dom';

interface SettingsPanelProps {
  user: User;
}

const SettingsPanel: React.FC<SettingsPanelProps> = ({ user }) => {
  const [activeTab, setActiveTab] = useState<'api' | 'smtp'>('api');
  const [apiKey, setApiKey] = useState('');
  const [apiUrl, setApiUrl] = useState('https://api.openai.com/v1/chat/completions');
  const [model, setModel] = useState('gpt-3.5-turbo');
  const [temperature, setTemperature] = useState(0.7);
  const [topP, setTopP] = useState(0.9);
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Vérifier si l'utilisateur a les droits d'administration
  const isAdmin = user.role === 'admin';
  
  const handleSaveSettings = async () => {
    if (!isAdmin) return;
    
    setIsLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      // Tester la connexion à l'API
      await testApiConnection(apiKey, apiUrl, model, temperature, topP);
      
      // Sauvegarder la clé API
      await saveApiKey(apiKey);
      
      setSuccess('Paramètres sauvegardés avec succès !');
    } catch (error: any) {
      setError(error.message || 'Une erreur est survenue lors de la sauvegarde des paramètres.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="text-center py-10">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Accès restreint</h3>
        <p className="text-gray-600">
          Vous n'avez pas les droits nécessaires pour accéder à cette page.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('api')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'api'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Configuration API
            </button>
            <button
              onClick={() => setActiveTab('smtp')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'smtp'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Configuration SMTP
            </button>
          </nav>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <Link
          to="/admin/seo"
          className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-all duration-300 flex items-center space-x-4 border border-gray-200"
        >
          <div className="bg-indigo-100 p-3 rounded-full">
            <Globe className="w-6 h-6 text-indigo-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Paramètres SEO</h3>
            <p className="text-sm text-gray-500">Gérer les méta-données des pages</p>
          </div>
        </Link>

        <Link
          to="/admin/users"
          className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-all duration-300 flex items-center space-x-4 border border-gray-200"
        >
          <div className="bg-indigo-100 p-3 rounded-full">
            <Mail className="w-6 h-6 text-indigo-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Gestion des utilisateurs</h3>
            <p className="text-sm text-gray-500">Gérer les comptes utilisateurs</p>
          </div>
        </Link>
      </div>

      {activeTab === 'api' ? (
        <>
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Configuration API</h2>
          
          {error && (
            <div className="bg-red-50 text-red-600 p-4 rounded-md mb-6 flex items-center">
              <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />
              <p>{error}</p>
            </div>
          )}
          
          {success && (
            <div className="bg-green-50 text-green-600 p-4 rounded-md mb-6 flex items-center">
              <Check className="w-5 h-5 mr-2 flex-shrink-0" />
              <p>{success}</p>
            </div>
          )}
          
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Configuration de l'API</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Clé API OpenAI
                </label>
                <input
                  type="password"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="sk-..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  Votre clé API OpenAI est stockée localement et n'est jamais partagée.
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  URL de l'API
                </label>
                <input
                  type="text"
                  value={apiUrl}
                  onChange={(e) => setApiUrl(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Modèle
                </label>
                <select
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
                  <option value="gpt-4">GPT-4</option>
                  <option value="gpt-4-turbo">GPT-4 Turbo</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Température ({temperature})
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={temperature}
                  onChange={(e) => setTemperature(parseFloat(e.target.value))}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-gray-500">
                  <span>Précis</span>
                  <span>Créatif</span>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Top P ({topP})
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={topP}
                  onChange={(e) => setTopP(parseFloat(e.target.value))}
                  className="w-full"
                />
              </div>
              
              <div className="pt-4">
                <button
                  onClick={handleSaveSettings}
                  disabled={isLoading || !apiKey}
                  className="bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 transition-colors flex items-center space-x-2 disabled:opacity-50"
                >
                  {isLoading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  ) : (
                    <>
                      <Save className="w-5 h-5" />
                      <span>Sauvegarder les paramètres</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </>
      ) : (
        <SmtpSettingsPanel />
      )}
    </div>
  );
};

export default SettingsPanel;